import { useEffect, useState, useContext } from "react";
import { Grid, InputBase, Switch, Tooltip, Button, Select, MenuItem } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import toast from "react-hot-toast";

import CloseIcon from "images/svg/Close.svg";
import EditIcon from "images/svg/edit.svg";
import PrinterIcon from "images/svg/printer-grey.svg";
import PrintBarcodeDialog from "../PrintBarcodeDialog/PrintBarcodeDialog";

import ItemService from "services/ItemService";
import ProductContext from "context/ProductContext";
import ShopContext from "context/ShopContext";
import { DEFAULT_PRODUCT_IMAGE, PRODUCT_TYPE, STAFF_PERMISSION } from "const";
import { getApiErrorMessage } from "../../../utils/errorHandlingMessageUtils";

import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import CustomTextField from "components/utils/CustomFields/CustomTextField";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";

import styles from "./styles.module.css";
import ProductPlaceholder from "images/product-placeholder.png";
import { usePermission } from "hooks/usePermission";

/* =========================================================
   Product Details Popup
   ========================================================= */

export default function EditItemDetailsPopup({ open, item, onClose = () => {}, onUpdate = () => {} }) {
  /* ---------------- Context ---------------- */

  const { categories } = useContext(ProductContext);
  const { selectedShop } = useContext(ShopContext);

  /* ---------------- State ---------------- */

  const [local, setLocal] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isBarcodeOpen, setIsBarcodeOpen] = useState(false);
  const { checkPermissionAndExecute, PermissionDialog } = usePermission();

  /* ---------------- Constants ---------------- */

  const unitOptions = selectedShop?.units?.split(",") || [];

  /* ---------------- Helpers ---------------- */

  const handleImageError = (e) => {
    e.target.src = ProductPlaceholder;
  };

  const getCategoryName = (id) => {
    const cat = categories?.find((c) => c.id === Number(id));
    return cat?.name || "-";
  };

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    if (!open) return;
    setLocal(item ? { ...item } : null);
    setIsEditing(false);
    setIsBarcodeOpen(false);
  }, [open, item]);

  /* ---------------- Handlers ---------------- */

  const handleFocus = (e) => {
    if (e.target && e.target.select) {
      e.target.select();
    }
  };

  const handleChange = (field) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;

    setLocal((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!local) return;

    const price = Number(local.price) || 0;
    const mrp = Number(local.mrp) || 0;
    const leastPrice = Number(local.least_price) || 0;
    const tax = Number(local.tax) || 0; // Base tax (GST)
    const cess = Number(local.cess) || 0; // Additional tax

    /* -------- Validations -------- */

    if (mrp > 0 && price > mrp) {
      toast.error("Selling price cannot be greater than MRP");
      return;
    }

    if (leastPrice > 0 && price < leastPrice) {
      toast.error("Selling price cannot be less than the minimum allowed price");
      return;
    }

    if (cess < 0) {
      toast.error("Additional tax cannot be negative");
      return;
    }

    /* -------- Save -------- */

    setIsSaving(true);

    try {
      const payload = {
        ...local,
        price,
        mrp,
        least_price: leastPrice,
        stock: Number(local.stock) || 0,
        order: Number(local.order) || 1,

        tax, // GST
        cess, // Additional tax
        total_tax: tax + cess,
      };

      const res = await ItemService.updateItem(local.id, payload);
      const updated = res?.data ?? payload;

      setLocal({ ...local, ...updated });
      setIsEditing(false);
      onUpdate(updated);
      toast.success("Product updated successfully!");
      onClose();
    } catch (err) {
      toast.error(`Failed to update product: ${getApiErrorMessage(err)}`);
    } finally {
      setIsSaving(false);
    }
  };

  /* ---------------- Renderer ---------------- */

  const renderCell = (label, fieldKey, type = "text", highlight = false, hasInfo = false) => {
    let value = local?.[fieldKey];

    if (fieldKey === "category") {
      value = getCategoryName(local?.category);
    }

    let isEditable = isEditing && !hasInfo;

    // Special case: Stock is only editable if Inventory Type is 'INVENTORY'
    if (fieldKey === "stock" && local?.type !== PRODUCT_TYPE.INVENTORY) {
      isEditable = false;
      if (isEditing) hasInfo = true;
    }

    return (
      <div className={styles.tableCell}>
        <span className={styles.label}>{label}</span>

        <div className={`${styles.value} ${(highlight || hasInfo) && isEditing ? styles.valueRed : ""}`}>
          {isEditable ? (
            fieldKey === "category" ? (
              <Select
                size="small"
                fullWidth
                value={local?.category ?? ""}
                onChange={handleChange("category")}
                className={styles.dropdownInput}
              >
                {categories?.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            ) : type === "boolean" ? (
              <Switch size="small" checked={!!value} onChange={handleChange(fieldKey)} />
            ) : type === "select" ? (
              <Select
                size="small"
                fullWidth
                value={value ?? ""}
                onChange={handleChange(fieldKey)}
                displayEmpty
                className={styles.dropdownInput}
              >
                {unitOptions.map((u) => (
                  <MenuItem key={u} value={u.trim()}>
                    {u.trim()}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <InputBase
                fullWidth
                type={type === "number" ? "number" : "text"}
                value={value ?? ""}
                onChange={handleChange(fieldKey)}
                onFocus={handleFocus}
                onClick={handleFocus}
                className={styles.seamlessInput}
                inputProps={{ style: { textAlign: "right" } }}
              />
            )
          ) : (
            <span className={hasInfo && isEditing ? styles.disabledText : ""}>
              {type === "boolean" ? (value ? "Yes" : "No") : (value ?? "-")}
            </span>
          )}

          {hasInfo && (
            <Tooltip title="This field cannot be edited in POS. To make changes, please use the Web Application" arrow>
              <InfoOutlinedIcon style={{ fontSize: 16, color: "#ccc", marginLeft: 4 }} />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  /* ---------------- Guard ---------------- */

  if (!open || !local) return null;

  /* ---------------- JSX ---------------- */

  return (
    <div className={styles.popupOverlay}>
      <div className={styles.dialogContent}>
        {isSaving ? (
          <SkeletonLoading isOpen height={580} count={2} />
        ) : (
          <Grid container className={styles.mainGrid}>
            <Grid item xs={12} sm={5} md={5} className={styles.imageSection}>
              <img
                src={local.image && local.image !== DEFAULT_PRODUCT_IMAGE ? local.image : ProductPlaceholder}
                alt={local.name}
                style={{
                  width: "100%",
                  maxHeight: 450,
                  objectFit: "contain",
                }}
                onError={handleImageError}
              />
            </Grid>

            <Grid item xs={12} sm={7} md={7} className={styles.rightGridItem}>
              <div className={styles.actionHeader}>
                {isEditing ? (
                  <>
                    <ButtonGray onClick={() => setIsEditing(false)}>Discard</ButtonGray>
                    <ButtonRed onClick={handleSave}>Update</ButtonRed>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      className={styles.buttonGray}
                      startIcon={<img src={PrinterIcon} alt="Print Barcode" className={styles.iconSmall} />}
                      onClick={() => setIsBarcodeOpen(true)}
                    >
                      Print Barcode
                    </Button>
                    <Button
                      variant="outlined"
                      className={styles.buttonGray}
                      startIcon={<img src={EditIcon} alt="Edit" className={styles.iconSmall} />}
                      onClick={() =>
                        checkPermissionAndExecute(STAFF_PERMISSION.PRODUCT_EDIT, () => setIsEditing(true), "Edit Product")
                      }
                    >
                      Edit
                    </Button>
                  </>
                )}

                <img src={CloseIcon} alt="Close" onClick={() => onClose(local)} className={styles.closeIcon} />
              </div>

              <div className={styles.rightSection}>
                {isEditing ? (
                  <div className={styles.nameEditWrapper}>
                    <CustomTextField
                      label="Item Name"
                      fullWidth
                      value={local.name ?? ""}
                      onChange={handleChange("name")}
                      onFocus={handleFocus}
                      onClick={handleFocus}
                      placeholder="Item Name"
                    />
                  </div>
                ) : (
                  <h1 className={styles.name}>{local.name}</h1>
                )}
                {isEditing ? (
                  <div className={styles.descriptionEditWrapper}>
                    <CustomTextField
                      multiline
                      rows={3}
                      fullWidth
                      value={local.description ?? ""}
                      onChange={handleChange("description")}
                      onFocus={handleFocus}
                      onClick={handleFocus}
                      placeholder="Description"
                    />
                  </div>
                ) : (
                  <p className={styles.description}>{local.description || "No description available."}</p>
                )}

                <div className={styles.separator} />

                <div className={styles.infoTable}>
                  <div className={styles.tableRow}>
                    {renderCell("Item PLU", "plu", "text", true, true)}
                    {renderCell("Category", "category", "select")}
                  </div>
                  <div className={styles.tableRow}>
                    {renderCell("Unit", "unit", "select")}
                    {renderCell("Price", "price", "number")}
                  </div>

                  <div className={styles.tableRow}>
                    {renderCell("MRP", "mrp", "number")}
                    {renderCell("Tax", "tax", "number")}
                  </div>

                  <div className={styles.tableRow}>
                    {/* Additional Tax → maps to `cess` */}
                    {renderCell("Additional Tax", "cess", "number")}
                    {renderCell("Stock", "stock", "number")}
                  </div>

                  <div className={styles.tableRow}>
                    {renderCell("Cost", "cost", "number")}
                    {renderCell("Least Price", "least_price", "number")}
                  </div>

                  <div className={styles.tableRow}>
                    {renderCell("HSN Code", "hsn", "text")}
                    {renderCell("Barcode", "ean", "text")}
                  </div>

                  <div className={styles.tableRow}>
                    {renderCell("Stock On Hold", "stock_on_hold", "number", true, true)}
                    {renderCell("Inventory Type", "type", "text", true, true)}
                  </div>

                  <div className={styles.tableRow}>
                    {renderCell("Available Days", "available_days", "text", true, true)}
                    {renderCell("Available Time", "available_time", "text", true, true)}
                  </div>

                  <div className={styles.tableRow}>
                    {renderCell("Can Sale", "is_available", "boolean")}
                    {renderCell("Export To Scale", "export_to_scale", "boolean")}
                  </div>
                  <div className={styles.tableRow}>
                    {renderCell("Display Order", "order", "number")}
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        )}
      </div>
      <PrintBarcodeDialog
        open={isBarcodeOpen}
        onClose={() => setIsBarcodeOpen(false)}
        item={local}
      />
      <PermissionDialog />
    </div>
  );
}
